/**
 * Workspace Tab Bar - Browser-style ticket tabs with priority indicator
 */

import React, { useState } from 'react';
import { Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAgentWorkspaceTabs, TabPriority } from '../contexts/AgentWorkspaceTabsContext';

interface WorkspaceTabBarProps {
  compact?: boolean;
}

function getPriorityBorderColor(priority?: TabPriority): string {
  switch (priority) {
    case 'high':
    case 'urgent':
      return '#ff4d4f';
    case 'medium':
      return '#faad14';
    case 'low':
      return '#52c41a';
    default:
      return '#d9d9d9';
  }
}

export const WorkspaceTabBar: React.FC<WorkspaceTabBarProps> = ({ compact }) => {
  const navigate = useNavigate();
  const ctx = useAgentWorkspaceTabs();
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);

  if (!ctx) return null;

  const { tabs, activeTabId, removeTab, switchToTab } = ctx;
  if (tabs.length < 1) return null;

  const handleClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    const idx = tabs.findIndex((t) => t.id === tabId);
    const nextTab = idx > 0 ? tabs[idx - 1] : tabs[idx + 1];
    removeTab(tabId);
    if (tabId === activeTabId) {
      if (nextTab) switchToTab(nextTab.id);
      else navigate('/tickets');
    }
  };

  const pad = compact ? '10px 18px' : '12px 20px';
  const minW = compact ? 120 : 140;
  const maxW = compact ? 230 : 280;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,
        flexWrap: 'wrap',
        backgroundColor: 'transparent',
        padding: 0,
        borderRadius: 0,
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        const isHovered = tab.id === hoveredTabId;
        const borderColor = getPriorityBorderColor(tab.priority);
        const isTicketNumberLabel = tab.label.startsWith('#');

        const tabWidth = isTicketNumberLabel
          ? Math.min(Math.max((tab.label.length * 9) + (compact ? 68 : 78), minW), 360)
          : Math.min(Math.max((tab.label.length * 7.5) + (compact ? 74 : 84), minW), maxW);

        return (
          <div
            key={tab.id}
            onClick={() => switchToTab(tab.id)}
            onMouseEnter={() => setHoveredTabId(tab.id)}
            onMouseLeave={() => setHoveredTabId(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: pad,
              cursor: 'pointer',
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
              borderLeft: `4px solid ${borderColor}`,
              borderTop: isActive ? '1px solid #e8e8e8' : '1px solid #e2e7ee',
              borderRight: isActive ? '1px solid #e8e8e8' : '1px solid #e2e7ee',
              backgroundColor: isActive ? '#fff' : '#f7f9fc',
              minWidth: tabWidth,
              maxWidth: tabWidth,
              flex: '0 0 auto',
              boxShadow: isActive ? '0 -2px 8px rgba(0,0,0,0.08)' : 'none',
              marginBottom: -1,
              position: 'relative',
              zIndex: isActive ? 2 : 1,
            }}
          >
            <Typography.Text
              strong={false}
              ellipsis={!isTicketNumberLabel}
              title={tab.label}
              style={{
                fontSize: compact ? 14 : 15,
                fontWeight: isActive ? 600 : 500,
                flex: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </Typography.Text>
            <span
              onClick={(e) => handleClose(e, tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                borderRadius: 4,
                cursor: 'pointer',
                color: '#666',
                flexShrink: 0,
                opacity: isHovered || isActive ? 1 : 0,
                transition: 'opacity 0.15s ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#e0e0e0';
                (e.currentTarget as HTMLElement).style.color = '#333';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLElement).style.color = '#666';
              }}
            >
              <CloseOutlined style={{ fontSize: 12 }} />
            </span>
          </div>
        );
      })}
    </div>
  );
};
